/**
 * CTMS task catalog — single source for role defaults (site-wide) and
 * trial delegation (Delegation of Authority log). ICH E6(R3): staff may
 * only perform trial tasks the PI delegated to them.
 */
export type CtmsTask =
  | 'view_patients'
  | 'edit_history'
  | 'record_vitals'
  | 'manage_visits'
  | 'consent'
  | 'ecrf_entry'
  | 'ecoa'
  | 'sdv'
  | 'query_resolution'
  | 'randomise'
  | 'dispense_ip'
  | 'report_ae'
  | 'protocol_deviation'
  | 'regulatory_files';

export const CTMS_TASKS: { key: CtmsTask; label: string }[] = [
  { key: 'view_patients', label: 'View patients' },
  { key: 'edit_history', label: 'Edit medical history' },
  { key: 'record_vitals', label: 'Record vital signs' },
  { key: 'manage_visits', label: 'Manage visits' },
  { key: 'consent', label: 'Informed Consent' },
  { key: 'ecrf_entry', label: 'eCRF data entry' },
  { key: 'ecoa', label: 'eCOA assessments' },
  { key: 'sdv', label: 'Source document review (SDV)' },
  { key: 'query_resolution', label: 'Query resolution' },
  { key: 'randomise', label: 'Randomise patients' },
  { key: 'dispense_ip', label: 'IP — dispense / drug accountability' },
  { key: 'report_ae', label: 'Safety/PV — AE / SAE reporting' },
  { key: 'protocol_deviation', label: 'Protocol deviations' },
  { key: 'regulatory_files', label: 'Regulatory files' },
];

/** Legacy free-text DOA labels mapped to catalog keys. */
const LEGACY: Record<string, CtmsTask> = {
  'Informed Consent': 'consent',
  'AE Reporting': 'report_ae',
  'SAE Reporting': 'report_ae',
  'Data Entry': 'ecrf_entry',
  'Source Document Review': 'sdv',
  'Query Resolution': 'query_resolution',
  'Protocol Deviation Management': 'protocol_deviation',
  'Vital Signs Collection': 'record_vitals',
  'Drug Accountability': 'dispense_ip',
};

export function normalizeTask(t: string): CtmsTask | null {
  if (CTMS_TASKS.some((x) => x.key === t)) return t as CtmsTask;
  return LEGACY[t] ?? null;
}

export function taskLabel(t: string): string {
  const k = normalizeTask(t);
  return CTMS_TASKS.find((x) => x.key === k)?.label ?? t;
}

const ALL: CtmsTask[] = CTMS_TASKS.map((t) => t.key);
const CLINICAL: CtmsTask[] = ['view_patients', 'edit_history', 'record_vitals', 'manage_visits', 'consent', 'ecrf_entry', 'ecoa', 'report_ae', 'protocol_deviation', 'query_resolution'];

/** Roles that always hold every task at their site. */
export const FULL_ACCESS_ROLES = ['principal_investigator', 'site_director'];

/** Site-wide starter defaults per role. Unknown roles get view_patients only. */
export const ROLE_DEFAULTS: Record<string, CtmsTask[]> = {
  principal_investigator: ALL,
  site_director: ALL,
  sub_investigator: [...CLINICAL, 'randomise', 'sdv'],
  research_doctor: [...CLINICAL, 'randomise'],
  research_nurse: ['view_patients', 'edit_history', 'record_vitals', 'manage_visits', 'consent', 'ecoa', 'report_ae'],
  clinical_research_coordinator: [...CLINICAL, 'randomise', 'regulatory_files'],
  study_coordinator: [...CLINICAL, 'randomise', 'regulatory_files'],
  clinic_coordinator: ['view_patients', 'manage_visits', 'record_vitals'],
  research_assistant: ['view_patients', 'record_vitals', 'manage_visits', 'ecoa'],
  clinical_trial_assistant: ['view_patients', 'manage_visits', 'regulatory_files'],
  data_manager: ['view_patients', 'ecrf_entry', 'sdv', 'query_resolution'],
  data_monitor: ['view_patients', 'sdv', 'query_resolution'],
  clinical_research_associate: ['view_patients', 'sdv', 'query_resolution'],
  quality_control_officer: ['view_patients', 'sdv', 'query_resolution', 'protocol_deviation'],
  quality_manager: ['view_patients', 'sdv', 'protocol_deviation', 'regulatory_files'],
  pharmacist: ['view_patients', 'dispense_ip'],
  pharmacist_of_record: ['view_patients', 'dispense_ip', 'randomise'],
  regulatory_specialist: ['view_patients', 'regulatory_files'],
  regulatory_officer: ['view_patients', 'regulatory_files'],
  laboratory_lead: ['view_patients', 'record_vitals'],
  crs_leader: ALL,
  project_manager: ['view_patients', 'regulatory_files', 'protocol_deviation'],
  operations_manager: ['view_patients', 'manage_visits', 'regulatory_files'],
  community_engagement_liaison: ['view_patients'],
  community_outreach_worker: ['view_patients'],
  recruitment_retention_team_lead: ['view_patients', 'manage_visits'],
};

/** Patient-page sub-tabs → task that unlocks them. Tabs not listed are always shown. */
export const TAB_TASK: Record<string, CtmsTask> = {
  vitals: 'record_vitals',
  visits: 'manage_visits',
  'adverse-events': 'report_ae',
  ecrf: 'ecrf_entry',
  consent: 'consent',
  ecoa: 'ecoa',
  sdv: 'sdv',
  randomization: 'randomise',
  'ip-management': 'dispense_ip',
};

/** Sub-tabs that only make sense inside a selected trial. */
export const TRIAL_ONLY_TABS = new Set(['ecrf', 'ecoa', 'sdv', 'randomization', 'ip-management']);

/** Sites where task-based tab hiding is switched on (pilot rollout). */
export const CTMS_ACCESS_PILOT_SITES = new Set<string>([
  'fac1d2cf-2681-4fb6-b168-ff9a8b766a89', // PTC BRIDGE
]);

/** Tabs gated at every site (not just pilot): IP needs dispense_ip, Safety/PV needs report_ae. */
export const ALWAYS_GATED_TABS = new Set(['ip-management', 'adverse-events']);

/** Site-dashboard tabs → task that unlocks them at every site. */
export const SITE_TAB_TASK: Record<string, CtmsTask> = {
  ip: 'dispense_ip',
  safety: 'report_ae',
};

/**
 * Resolve allowed tasks. Trial view: role defaults ∩ delegated (site-wide or that trial).
 * IP and Safety/PV always require a current signed delegation for non-authorities.
 * A site overview accepts duties in any trial; subject pages require that trial's scope.
 */
export function resolveTasks(args: {
  role: string | null;
  isPrimary: boolean;
  isAdmin: boolean;
  trialId: string | null;
  delegations: { trial_id: string | null; delegated_tasks: string[] }[];
}): Set<CtmsTask> {
  const { role, isPrimary, isAdmin, trialId, delegations } = args;
  if (isAdmin || isPrimary || (role && FULL_ACCESS_ROLES.includes(role))) return new Set(ALL);
  const base = new Set<CtmsTask>(role ? ROLE_DEFAULTS[role] ?? ['view_patients'] : []);
  const delegated = new Set<CtmsTask>();
  for (const d of delegations) {
    if (trialId && d.trial_id && d.trial_id !== trialId) continue;
    for (const t of d.delegated_tasks || []) {
      const k = normalizeTask(t);
      if (k) delegated.add(k);
    }
  }
  delegated.add('view_patients');
  const result = !trialId || delegations.length === 0
    ? base
    : new Set([...base].filter((t) => delegated.has(t)));
  for (const task of ['dispense_ip', 'report_ae'] as const) {
    result.delete(task);
    if (role && delegated.has(task)) result.add(task);
  }
  return result;
}

export function delegationStatus(record: { status: string | null; effective_date: string; expiration_date: string | null }, today = new Date().toISOString().slice(0, 10)) {
  if (record.status === 'revoked') return 'revoked';
  if (record.status === 'expired' || (record.expiration_date && record.expiration_date < today)) return 'expired';
  if (record.effective_date > today) return 'scheduled';
  return record.status === 'active' ? 'active' : 'inactive';
}
